import React, { useEffect, useState } from 'react';
import { Tab, TabProps } from 'semantic-ui-react';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes';
import {
  FinanceResponse,
  UpstartApplicationStatus,
} from '@upstart/harmony/lib/legos/financingTypes';
import { FinanceResponseStatus } from '@upstart/harmony/lib/constants';
import { DealerWithFlags } from '@upstart/harmony/lib/legos/dealerTypes';
import { useAsyncCallback } from 'react-async-hook';
import { sleep } from '@upstart/harmony/lib/legos/utils/tsutils';

import UpstartOffer from './UpstartOffer';
import OfferReview from './OfferReview';
import DocumentUpload from './DocumentUpload';
import {
  isDMSFlowEnabled as isDMSFlowEnabledUtil,
  isSentToDMS,
} from '../../util/dmsUtils';
import { CustomerAAN } from './CustomerAAN';
import FinanceApplicationService from '../../services/FinanceApplicationService';

const MAX_ATTEMPTS = 5;

/**
 * If application status has an AAN url that = 'generating', fetch updated status a maximum of 5 times to see if it
 * has finished
 * @param initialStatus
 * @param applicationId
 */
const getFinalAANStatus = async (
  initialStatus: UpstartApplicationStatus,
  applicationId?: string,
) => {
  let attempts = 0;
  let status = initialStatus;

  while (
    status?.adverseActionNoticeInfo?.url === 'generating' &&
    attempts < MAX_ATTEMPTS
  ) {
    await sleep(1000);
    status = await FinanceApplicationService.getApplicationStatus(
      applicationId,
    );
    attempts += 1;
  }

  return status;
};

interface Props {
  deal: Deal;
  response: FinanceResponse | null;
  showRicTab: boolean;
  setShowRicTab: (isShown: boolean) => void;
  dealer: DealerWithFlags;
  status: UpstartApplicationStatus | null;
  reloadResponses: VoidFunction;
}

function RenderUpstartOffer({
  deal,
  response,
  showRicTab,
  setShowRicTab,
  dealer,
  status,
  reloadResponses,
}: Props) {
  const defaultTab = showRicTab ? 2 : 0;
  const [activeTab, setActiveTab] = useState<number | undefined | string>(
    defaultTab,
  );

  useEffect(() => {
    if (showRicTab && activeTab !== 2) {
      setActiveTab(2);
    } else {
      setActiveTab(0);
    }
  }, [showRicTab, response]);

  const handleRICCancel = () => setActiveTab(0);
  const handleTabChange = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    { activeIndex }: TabProps,
  ) => {
    setActiveTab(activeIndex);
    if (showRicTab) {
      setShowRicTab(false);
    }
  };
  const isDMSFlowEnabled = isDMSFlowEnabledUtil(dealer);
  const sentToDMS = isSentToDMS(deal);
  const selectedFinanceApplication = deal?.selectedFinanceResponseId;
  const isCurrentOfferSelected =
    response?.id &&
    deal?.selectedFinanceResponseId &&
    response?.id === deal?.selectedFinanceResponseId;
  const shouldDisplayRICTabForUpstartOffers = isDMSFlowEnabled
    ? sentToDMS && selectedFinanceApplication && isCurrentOfferSelected
    : selectedFinanceApplication;
  const isDenied = response?.status === FinanceResponseStatus.DENIED;
  const fetchFinalApplicationStatus = useAsyncCallback(getFinalAANStatus);

  useEffect(() => {
    if (status) {
      fetchFinalApplicationStatus.execute(status, response?.applicationId);
    }
    // eslint-disable-next-line
  }, [status]);

  const panes = [
    ...(response?.status !== FinanceResponseStatus.DENIED
      ? [
          {
            menuItem: 'Finance Offer',
            render: () => (
              <UpstartOffer
                dealId={deal.id}
                dealer={dealer}
                responseId={response?.id as any}
                reloadResponses={reloadResponses}
              />
            ),
          },
          {
            menuItem: 'Upload Documents',
            render: () => (
              <DocumentUpload
                dealId={deal.id}
                responseId={response?.id as any}
              />
            ),
          },
        ]
      : []),
  ];

  if (shouldDisplayRICTabForUpstartOffers) {
    panes.push({
      menuItem: 'Retail Installment Contract',
      render: () => (
        <OfferReview
          dealId={deal.id}
          responseId={response?.id as any}
          hideSideBarAndHeader
          onCancel={handleRICCancel}
        />
      ),
    });
  }

  if (isDenied && status && fetchFinalApplicationStatus.result) {
    panes.push({
      menuItem: 'Declined AAN',
      render: () => (
        <CustomerAAN
          aanUrl={
            fetchFinalApplicationStatus.result?.adverseActionNoticeInfo?.url
          }
        />
      ),
    });
  }

  return (
    <Tab
      className="offer-list-tabs"
      menu={{ secondary: true, pointing: true }}
      activeIndex={activeTab}
      panes={panes}
      onTabChange={handleTabChange}
    />
  );
}

export default RenderUpstartOffer;
